import React, { useState, useEffect } from 'react';
import { Box, Button, Card, CardActions, CardContent, Stack, CircularProgress, Typography } from '@mui/material';
import { Form } from 'components/_forms';
import { translate as t } from 'hooks/useTranslations';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { useDTBPackageContext } from '../context';
import DTBPackageFormFields from '../_components/DTBPackageFormFields';

const EditProperties: React.FC = () => {
  const { editedData, isLoading, isError, isSaving, handleSave } = useDTBPackageContext();

  const [packageName, setPackageName] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [manualId, setManualId] = useState('');
  const [verticalId, setVerticalId] = useState('');
  const [level, setLevel] = useState<'AGENCY' | 'JURISDICTION' | 'GLOBAL'>('GLOBAL');

  useEffect(() => {
    if (!editedData) return;
    setPackageName(editedData?.name || '');
    setManualId(editedData?.manual?.id || '');
    setJurisdictionId(editedData?.jurisdiction?.id || '');
    setVerticalId(editedData?.vertical?.id || '');
    setLevel(editedData?.level || 'GLOBAL');
  }, [editedData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editedData) return;
    const newData = {
      ...editedData,
      name: packageName,
      manual:
        level === 'AGENCY'
          ? editedData.manual
          : manualId
            ? { id: manualId, name: editedData.manual?.name ?? '' }
            : null,
      jurisdiction:
        level === 'AGENCY'
          ? editedData.jurisdiction
          : jurisdictionId
            ? { id: jurisdictionId, name: editedData.jurisdiction?.name, abbreviation: editedData.jurisdiction?.abbreviation }
            : null,
      vertical: verticalId ? { ...editedData.vertical, id: verticalId } : editedData.vertical,
      level,
    };
    await handleSave(newData);
  };

  return (
    <TakeoverLayout
      pageTitle={
        editedData?.name
          ? `${t('dtbs.packages.edit.title', 'Edit Package')} - ${editedData.name}`
          : t('dtbs.packages.edit.title', 'Edit Package')
      }
      backUrl='..'
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        {isLoading && (
          <Stack
            direction='row'
            justifyContent='center'
            sx={{ mb: 2 }}
          >
            <CircularProgress />
          </Stack>
        )}
        {isError && (
          <Typography
            color='error'
            sx={{ mb: 2 }}
          >
            {t('common.error')}
          </Typography>
        )}
        {!isLoading && !isError && (
          <Form onSubmit={handleSubmit}>
            <CardContent>
              <DTBPackageFormFields
                orgId={''}
                packageName={packageName}
                jurisdictionId={jurisdictionId}
                manualId={manualId}
                verticalId={verticalId}
                level={level}
                isNew={false}
                onPackageNameChange={setPackageName}
                onJurisdictionChange={setJurisdictionId}
                onManualChange={setManualId}
                onVerticalChange={setVerticalId}
                onLevelChange={setLevel}
              />
            </CardContent>
            <CardActions>
              <Box />
              <Stack
                direction='row'
                spacing={2}
              >
                <Button
                  color='neutral'
                  href='..'
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={isSaving || !packageName || (level === 'JURISDICTION' && !jurisdictionId)}
                  component='button'
                  type='submit'
                  variant='contained'
                  endIcon={isSaving ? <CircularProgress size={16} /> : undefined}
                >
                  {t('common.saveChanges', 'Save changes')}
                </Button>
              </Stack>
            </CardActions>
          </Form>
        )}
      </Card>
    </TakeoverLayout>
  );
};

export default EditProperties;
